import { useState, useEffect } from 'react';

const REMAINING_SLOTS = 3;

export default function CampaignSection() {
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setPulse(true);
      setTimeout(() => setPulse(false), 400);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="campaign" aria-label="初月980円キャンペーン" className="py-20 md:py-28 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/84cfed17c070e236571fdba8072779d8.jpg"
          alt="キャンペーン背景"
          className="w-full h-full object-cover object-top"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/95 via-stone-900/88 to-stone-900/70"></div>
      </div>

      {/* Decorative glow */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-amber-600/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8">
        <div className="max-w-2xl">

          {/* Top badge */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="inline-flex items-center gap-2 bg-amber-500 text-white text-xs font-bold px-4 py-2 rounded-full animate-pulse">
              <i className="ri-gift-line"></i>
              期間限定キャンペーン実施中
            </div>
            {/* Remaining slots */}
            <div className="inline-flex items-center gap-1.5 bg-red-500/90 text-white text-xs font-bold px-3 py-2 rounded-full">
              <span
                className={`w-2 h-2 rounded-full bg-white transition-opacity duration-200 ${pulse ? 'opacity-100' : 'opacity-40'}`}
              ></span>
              残り{REMAINING_SLOTS}枠のみ
            </div>
          </div>

          {/* Title */}
          <h2
            className="text-2xl md:text-4xl font-black text-white leading-tight mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            創業15周年・法人化10周年<br />
            <span className="text-amber-300">記念キャンペーン</span>
          </h2>

          {/* Price Highlight */}
          <div className="bg-white/10 backdrop-blur-sm border border-amber-400/30 rounded-2xl p-6 md:p-8 mb-8 relative overflow-hidden">
            {/* Corner ribbon */}
            <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-black px-4 py-1 rounded-bl-xl">
              約90%OFF
            </div>

            <p className="text-stone-300 text-sm mb-3">通常月額9,800円のところ</p>
            <div className="flex items-end gap-3 mb-4">
              <div>
                <span className="text-stone-400 text-sm line-through block mb-1">9,800円</span>
                <div className="flex items-end gap-1">
                  <span className="text-white text-lg">初月</span>
                  <span className="text-amber-300 text-5xl md:text-6xl font-black leading-none">980</span>
                  <span className="text-white text-lg mb-1">円</span>
                  <span className="text-stone-300 text-sm mb-1">（税込）</span>
                </div>
              </div>
            </div>

            {/* Savings callout */}
            <div className="bg-amber-500/20 border border-amber-400/30 rounded-lg px-4 py-2 mb-4 inline-flex items-center gap-2">
              <i className="ri-sparkling-line text-amber-300 text-sm"></i>
              <span className="text-amber-200 text-sm font-semibold">初月だけで8,820円もお得！</span>
            </div>

            <p className="text-stone-300 text-sm leading-relaxed">
              まずはお試しください。初月980円で実際のサービスをご体験いただけます。<br />
              2ヶ月目以降は通常料金（月額9,800円）となります。
            </p>
          </div>

          {/* Conditions */}
          <div className="mb-8 space-y-2">
            {[
              '岡山県南部にお住まいの方が対象です',
              '新規お申し込みの方限定のキャンペーンです',
              'キャンペーン期間中のお申し込みに限ります',
              '2ヶ月目以降の継続は任意です（いつでも解約可能）',
            ].map((cond) => (
              <div key={cond} className="flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-checkbox-circle-line text-amber-400 text-sm"></i>
                </div>
                <span className="text-stone-300 text-sm">{cond}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => scrollTo('contact')}
              className="cursor-pointer whitespace-nowrap bg-amber-500 hover:bg-amber-400 text-white font-bold px-8 py-4 rounded-full text-base transition-all flex items-center justify-center gap-2 hover:scale-105"
            >
              <i className="ri-phone-line"></i>
              980円でお試しを申し込む
            </button>
            <button
              onClick={() => scrollTo('price')}
              className="cursor-pointer whitespace-nowrap bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-full text-base transition-colors border border-white/30"
            >
              料金プランの詳細を見る
            </button>
          </div>

          <p className="text-stone-400 text-xs mt-4">
            ※ キャンペーンは予告なく終了する場合があります。残り枠がなくなり次第終了です。
          </p>
        </div>
      </div>
    </section>
  );
}
